import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

type CategoryStat = {
  id: string;
  name: string;
  icon: string;
  color: string;
  count: number;
  views: number;
  featured: number;
};

type DailyStat = {
  date: string;
  count: number;
};

export default function StatsTab() {
  const [catStats, setCatStats] = useState<CategoryStat[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalNews, setTotalNews] = useState(0);
  const [topNews, setTopNews] = useState<{ id: string; title: string; views: number; category: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [catRes, newsRes] = await Promise.all([
        supabase.from('categories').select('id, name, icon, color'),
        supabase.from('news').select('id, title, category, views, featured, created_at, date'),
      ]);

      if (catRes.data && newsRes.data) {
        const newsData = newsRes.data;
        const total = newsData.length;
        const views = newsData.reduce((s, n) => s + (n.views || 0), 0);
        setTotalNews(total);
        setTotalViews(views);

        // Category stats
        const mapped: CategoryStat[] = catRes.data.map(cat => {
          const catNews = newsData.filter(n => n.category === cat.id);
          return {
            id: cat.id,
            name: cat.name,
            icon: cat.icon,
            color: cat.color,
            count: catNews.length,
            views: catNews.reduce((s, n) => s + (n.views || 0), 0),
            featured: catNews.filter(n => n.featured).length,
          };
        }).filter(c => c.count > 0).sort((a, b) => b.views - a.views);
        setCatStats(mapped);

        // Top 5 news by views
        const sorted = [...newsData].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
        setTopNews(sorted.map(n => ({ id: n.id, title: n.title, views: n.views || 0, category: n.category })));

        // Daily stats — last 14 days
        const now = new Date();
        const days: DailyStat[] = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().split('T')[0];
          const count = newsData.filter(n => (n.created_at || '').startsWith(dateStr)).length;
          days.push({ date: dateStr, count });
        }
        setDailyStats(days);
      }
      setLoading(false);
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-20 text-gray-400">
        <i className="ri-loader-4-line text-3xl animate-spin block mb-3" />Yükleniyor...
      </div>
    );
  }

  const maxViews = Math.max(...catStats.map(c => c.views), 1);
  const maxCount = Math.max(...catStats.map(c => c.count), 1);
  const maxDaily = Math.max(...dailyStats.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Haber', value: totalNews.toLocaleString(), icon: 'ri-newspaper-line', color: '#0A0E27', bg: 'bg-[#0A0E27]/5' },
          { label: 'Toplam Görüntüleme', value: totalViews >= 1000 ? `${(totalViews / 1000).toFixed(1)}K` : totalViews.toString(), icon: 'ri-eye-line', color: '#00D9FF', bg: 'bg-[#00D9FF]/10' },
          { label: 'Aktif Kategori', value: catStats.length.toString(), icon: 'ri-apps-line', color: '#00FF88', bg: 'bg-[#00FF88]/10' },
          { label: 'Ort. Görüntüleme', value: totalNews > 0 ? Math.round(totalViews / totalNews).toLocaleString() : '0', icon: 'ri-bar-chart-line', color: '#FFB347', bg: 'bg-[#FFB347]/10' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-5">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${s.bg}`}>
              <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
            </div>
            <div className="text-2xl font-black text-[#0A0E27]">{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Views Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-black text-[#0A0E27] text-base">Kategoriye Göre Görüntüleme</h3>
              <p className="text-xs text-gray-400 mt-0.5">Tüm zamanlar</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#00D9FF] inline-block" />Görüntüleme</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FFB347] inline-block" />Haber Sayısı</span>
            </div>
          </div>
          <div className="space-y-3">
            {catStats.slice(0, 10).map(cat => (
              <div key={cat.id} className="group">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${cat.color}15` }}
                    >
                      <i className={`${cat.icon} text-[10px]`} style={{ color: cat.color }} />
                    </div>
                    <span className="text-sm font-semibold text-[#0A0E27]">{cat.name}</span>
                    <span className="text-[10px] text-gray-400">{cat.count} haber</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-bold text-[#0A0E27]">
                      {cat.views >= 1000 ? `${(cat.views / 1000).toFixed(1)}K` : cat.views}
                    </span>
                    {cat.featured > 0 && (
                      <span className="text-yellow-500 flex items-center gap-0.5">
                        <i className="ri-star-fill text-[10px]" />{cat.featured}
                      </span>
                    )}
                  </div>
                </div>
                {/* Dual bar */}
                <div className="relative h-5 bg-gray-50 rounded-lg overflow-hidden">
                  {/* Views bar */}
                  <div
                    className="absolute left-0 top-0 h-full rounded-lg transition-all duration-700"
                    style={{
                      width: `${(cat.views / maxViews) * 100}%`,
                      backgroundColor: `${cat.color}25`,
                    }}
                  />
                  {/* Count bar */}
                  <div
                    className="absolute left-0 top-1 bottom-1 rounded-md transition-all duration-700"
                    style={{
                      width: `${(cat.count / maxCount) * 100}%`,
                      backgroundColor: cat.color,
                      opacity: 0.7,
                    }}
                  />
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold"
                    style={{ color: cat.color }}
                  >
                    %{Math.round((cat.views / maxViews) * 100)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 5 News */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-black text-[#0A0E27] text-base mb-1">En Çok Okunan</h3>
          <p className="text-xs text-gray-400 mb-5">Tüm zamanlar top 5</p>
          <div className="space-y-4">
            {topNews.map((n, i) => {
              const cat = catStats.find(c => c.id === n.category);
              return (
                <div key={n.id} className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black flex-shrink-0"
                    style={{
                      backgroundColor: i === 0 ? '#FFB34720' : '#0A0E2710',
                      color: i === 0 ? '#FFB347' : '#0A0E27',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <a
                      href={`/haber/${n.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-[#0A0E27] line-clamp-2 hover:text-[#00D9FF] transition-colors cursor-pointer"
                    >
                      {n.title}
                    </a>
                    <div className="flex items-center gap-2 mt-1">
                      {cat && (
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: `${cat.color}15`, color: cat.color }}
                        >
                          {cat.name}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-eye-line text-[10px]" />
                        {n.views >= 1000 ? `${(n.views / 1000).toFixed(1)}K` : n.views}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Daily Publication Chart */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-black text-[#0A0E27] text-base">Günlük Yayın Aktivitesi</h3>
            <p className="text-xs text-gray-400 mt-0.5">Son 14 gün</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-[#0A0E27]">
              {dailyStats.reduce((s, d) => s + d.count, 0)}
            </div>
            <div className="text-xs text-gray-400">toplam haber</div>
          </div>
        </div>
        <div className="flex items-end gap-1.5 h-32">
          {dailyStats.map((d, i) => {
            const height = maxDaily > 0 ? Math.max(4, (d.count / maxDaily) * 100) : 4;
            const isToday = i === dailyStats.length - 1;
            const dateLabel = new Date(d.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="relative w-full flex items-end justify-center" style={{ height: '100px' }}>
                  {d.count > 0 && (
                    <div
                      className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity text-center text-[10px] font-bold text-[#0A0E27] -top-5"
                    >
                      {d.count}
                    </div>
                  )}
                  <div
                    className="w-full rounded-t-md transition-all duration-500"
                    style={{
                      height: `${height}%`,
                      backgroundColor: isToday ? '#00D9FF' : d.count > 0 ? '#0A0E2720' : '#0A0E2708',
                    }}
                  />
                </div>
                <span className={`text-[9px] font-medium ${isToday ? 'text-[#00D9FF]' : 'text-gray-300'}`}>
                  {isToday ? 'Bugün' : dateLabel.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
        {/* X axis labels */}
        <div className="flex items-center gap-1.5 mt-1">
          {dailyStats.map((d, i) => {
            const isToday = i === dailyStats.length - 1;
            const dateLabel = new Date(d.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
            return (
              <div key={d.date} className="flex-1 text-center">
                {(i === 0 || i === 6 || i === 13) && (
                  <span className={`text-[9px] ${isToday ? 'text-[#00D9FF] font-bold' : 'text-gray-300'}`}>
                    {isToday ? 'Bugün' : dateLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Pie-like breakdown */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="font-black text-[#0A0E27] text-base mb-1">Kategori Dağılımı</h3>
        <p className="text-xs text-gray-400 mb-5">Haber sayısına göre</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {catStats.map(cat => {
            const pct = totalNews > 0 ? Math.round((cat.count / totalNews) * 100) : 0;
            return (
              <div key={cat.id} className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${cat.color}15` }}
                >
                  <i className={`${cat.icon} text-base`} style={{ color: cat.color }} />
                </div>
                <div className="text-center">
                  <div className="text-lg font-black text-[#0A0E27]">{cat.count}</div>
                  <div className="text-[10px] text-gray-400 leading-tight">{cat.name}</div>
                  <div
                    className="text-[10px] font-bold mt-0.5"
                    style={{ color: cat.color }}
                  >
                    %{pct}
                  </div>
                </div>
                {/* Mini bar */}
                <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, backgroundColor: cat.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
